/** @jsx isolateComponent */
import { css } from '@emotion/core';
import {
  Animation,
  Colors,
  Heading,
  HeadingLevel,
  Img,
  Link,
  Typography,
} from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../universal-nav/hooks/useUniversalNavContext';
import type { ArticleStubType } from '../../../constants';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { ArrowLinkIcon } from '../MegaMenuSubmenu';
import type { AnchorTarget } from '@zapier/design-system';

type Props = ArticleStubType & {
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = {
  root: css({
    color: Colors.neutral800,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    maxWidth: 400,
    ...Typography.smallPrint1Bold,
    '@media (hover: hover)': {
      '&:hover p': {
        textDecoration: 'underline',
      },
      '&:hover a': {
        textDecoration: 'none',
      },
      '&:hover p > span': {
        marginLeft: 4,
        opacity: 1,
      },
      '&:focus-within': {
        outline: `1px solid ${Colors.blue}`,
      },
      '&:focus-within > a': {
        textDecoration: 'none',
      },
    },
  }),
  title: css({
    display: 'block',
    marginBottom: 20,
    textDecoration: 'none',
    color: Colors.neutral800,
    outline: 'none',
    // This overrides an scss style that exists in `z/z`
    borderBottom: 'none',
    '&:hover': {
      textDecoration: 'none',
      color: Colors.neutral800,
    },
    '&:focus': {
      textDecoration: 'none',
      color: Colors.neutral800,
    },
    // This pseudo element stretches over the entire PromoArticle
    // to make the entire PromoArticle clickable
    '&::before': {
      content: '""',
      position: 'absolute',
      zIndex: 1,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  }),
  imageContainer: css({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  }),
  description: css({
    transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    // All of the properties below this are necessary
    // for line clamping to work. Even though the properties
    // have 'webkit' in them, they work cross-browser.
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    ...Typography.smallPrint1,
  }),
};

/**
 * Article with an Image and description
 */
export const ArticlePromo = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `ArticlePromo: ${text}`,
    });
    if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
  };

  return (
    <article css={Styles.root}>
      <HeadingLevel>
        <Heading>
          <Link
            color={null}
            css={Styles.title}
            href={getFullyQualifiedURL(props.href) || undefined}
            onClick={() => handleEvent(props.text, 'click')}
            onMouseEnter={() => handleEvent(props.text, 'hover')}
            target={props.target as AnchorTarget}
          >
            {props.text}
          </Link>
        </Heading>
      </HeadingLevel>
      <div css={Styles.imageContainer}>
        <Img
          alt={props.altText}
          borderRadius={0}
          src={props.imageUrl}
          width={100}
        />
        <p css={Styles.description}>
          {props.description}
          <ArrowLinkIcon />
        </p>
      </div>
    </article>
  );
};
